import { Table, Button } from "react-bootstrap";
import CustomModal from "../../../components/customModal/CustomModal";
import { useEffect, useState } from "react";
import useMckesson from "../../../Hooks/useMckesson";
import useApprovalWorkflow from "../../../Hooks/useApprovalWorkflow";
import Loading from "../../../components/LoadingPage/Loading";
import moment from "moment";
import { toast } from "react-toastify";

export const ApprovalItems = ({
  showApprovalModal,
  toggleApprovalModal,
  orderId,
  orderNo,
  onApprovalUpdate,
}: {
  showApprovalModal: boolean;
  toggleApprovalModal: () => void;
  orderId: string;
  orderNo: string;
  onApprovalUpdate?: () => void;
}) => {
  const [approvalItems, setApprovalItems] = useState<any[]>([]);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [notes, setNotes] = useState("");
  const [approvalMode, setApprovalMode] = useState<string>("");
  const {
    getMckessonWorkflowItemsAPI,
    getMckessonWorkflowItemsLoading,
    putMckessonApproveRejectWorkflowItemAPI,
    putMckessonApproveRejectWorkflowItemLoading,
  } = useMckesson();
  const { getApprovalWorkflowByIdAPI } = useApprovalWorkflow();

  const loggedInUserId = localStorage.getItem("id");

  const fetchApprovalItems = () => {
    getMckessonWorkflowItemsAPI({ key: orderId }).then((res: any) => {
      if (res) {
        const sorted = [...res].sort(
          (a: any, b: any) =>
            (a.approverDetail?.approvalOrder ?? 0) -
            (b.approverDetail?.approvalOrder ?? 0)
        );
        setApprovalItems(sorted);

        // Fetch workflow to determine approval mode (Sequential/Parallel)
        const workflowId = res[0]?.approvalWorkflowId;
        if (workflowId) {
          getApprovalWorkflowByIdAPI(workflowId).then((workflow: any) => {
            setApprovalMode(workflow?.approvalMode?.mode?.toLowerCase() ?? "");
          }).catch(() => {
            setApprovalMode("");
          });
        }
      }
    });
  };

  const isActionEnabled = (index: number): boolean => {
    if (index === null) return false;

    const item = approvalItems[index];
    if (!item) return false;

    // Already acted on
    if (item.isApproved === true || item.isApproved === false) return false;

    // Check if logged-in user matches the approver
    const isCurrentUserApprover = item.approverUserId === loggedInUserId || item.user?.id === loggedInUserId;
    if (!isCurrentUserApprover) return false;

    if (approvalMode === "sequential") {
      // All previous approvers (lower approvalOrder) must have approved
      const currentOrder = item.approverDetail?.approvalOrder ?? 0;
      const allPreviousApproved = approvalItems
        .filter((ai: any) => (ai.approverDetail?.approvalOrder ?? 0) < currentOrder)
        .every((ai: any) => ai.isApproved === true);
      return allPreviousApproved;
    }

    // Parallel: no sequence check needed
    return true;
  };

  useEffect(() => {
    if (showApprovalModal && orderId) {
      fetchApprovalItems();
    }
    if (!showApprovalModal) {
      setSelectedRowIndex(null);
      setNotes("");
      setApprovalMode("");
    }
  }, [showApprovalModal, orderId]);

  const handleApproveReject = async (isApproved: boolean) => {
    if (selectedRowIndex === null) return;
    try {
      const selectedItem = approvalItems[selectedRowIndex];
      const updatedItem = {
        id: selectedItem.id,
        isApproved,
        notes,
      };
      await putMckessonApproveRejectWorkflowItemAPI(updatedItem);
      toast.success(isApproved ? "Order has been Approved." : "Order has been Rejected.");
      fetchApprovalItems();
      onApprovalUpdate?.();
    } catch (err) {
      // error handled in hook
    }
  };

  return (
    <>
      {(getMckessonWorkflowItemsLoading || putMckessonApproveRejectWorkflowItemLoading) && (
        <Loading />
      )}
      {showApprovalModal && (
        <CustomModal
          id="approvalItemsModal"
          title="Approval Items"
          content={
            <div className="modal--content--wrapper">
              <div className="modal--inputbox">
                <Table>
                  <thead>
                    <tr>
                      <th>PO #</th>
                      <th>Approver</th>
                      <th>Email</th>
                      <th>Sequence</th>
                      <th>Approved?</th>
                      <th>Timestamp</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {approvalItems?.length > 0 ? (
                      approvalItems.map((item: any, index: number) => (
                        <tr
                          key={index}
                          className={selectedRowIndex === index ? "selected-row" : ""}
                          onClick={() => {
                            setSelectedRowIndex(index);
                            setNotes(item.notes ?? "");
                          }}
                          style={{ cursor: "pointer" }}
                        >
                          <td>{orderNo || "-"}</td>
                          <td>
                            {item.user?.userName ||
                              (item.user?.fName || item.user?.lName
                                ? `${item.user?.fName ?? ""} ${item.user?.lName ?? ""}`.trim()
                                : "-")}
                          </td>
                          <td>{item.user?.email ?? "-"}</td>
                          <td>{item.approverDetail?.approvalOrder ?? "-"}</td>
                          <td>
                            {item.isApproved === true
                              ? "Yes"
                              : item.isApproved === false
                                ? "No"
                                : "-"}
                          </td>
                          <td>
                            {item.timestamp
                              ? moment(item.timestamp).format(
                                "MM/DD/YYYY HH:mm:ss"
                              )
                              : ""}
                          </td>
                          <td>{item.notes ?? ""}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7} style={{ textAlign: "center" }}>
                          No records found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
                <p className="record-count-text">
                  {approvalItems?.length ?? 0} record(s)
                </p>
                {selectedRowIndex !== null && (
                  <div style={{ padding: "12px 16px" }}>
                    <label htmlFor="approvalNotes" className="form-label fw-bold">
                      Notes:
                    </label>
                    <textarea
                      id="approvalNotes"
                      className="form-control"
                      rows={3}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      disabled={selectedRowIndex === null || !isActionEnabled(selectedRowIndex!)}
                    />
                  </div>
                )}
              </div>
            </div>
          }
          footer={
            <div className="modal--action--btn--wrapper text-end">
              <Button
                type="button"
                variant="primary"
                disabled={selectedRowIndex === null || !isActionEnabled(selectedRowIndex!)}
                onClick={() => handleApproveReject(true)}
              >
                Approve
              </Button>
              <Button
                type="button"
                variant="danger"
                disabled={selectedRowIndex === null || !isActionEnabled(selectedRowIndex!)}
                onClick={() => handleApproveReject(false)}
              >
                Reject
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={toggleApprovalModal}
              >
                Cancel
              </Button>
            </div>
          }
          onClose={toggleApprovalModal}
        />
      )}
    </>
  );
};
